// @flow
import React from 'react';
import Papa from 'papaparse';
import _ from 'lodash';

import FormCellInput from 'components/FormCellInput';
import Text from 'components/Text';
import Formsy from 'lib/Formsy';

import { userRowEmpty, addValidationErrors } from 'reducers/directAddForm';

import type { UserRow } from 'reducers/directAddForm';

import './UsersParticipantsTableInputRow.scss';

type Props = {
  autofocus?: boolean,
  namePlaceholder: string,
  number: number,
  onChange: Function,
  supportSms: boolean,
  userRow: UserRow,
  emailRequired: boolean,
  onPaste: Function,
};

type State = {
  primaryDeviceStored: boolean,
  nameStored: boolean,
  secondaryDeviceStored: boolean,
  validateBecauseFocusLost: boolean,
};

export default class UsersParticipantsTableInputRow extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      primaryDeviceStored: true,
      nameStored: true,
      secondaryDeviceStored: true,
      validateBecauseFocusLost: false,
    };
  }

  componentDidUpdate(prevProps: Props) {
    const { validateBecauseFocusLost } = this.state;
    const { userRow } = this.props;

    // Should a user click off the row before entering the primaryDevice field,
    // we need to add an error to the device
    if (validateBecauseFocusLost && prevProps.userRow !== userRow) {
      if (!userRow.primaryDevice.value && !userRowEmpty(userRow)) {
        this.validateAndStoreRow(userRow);
      }
      this.resetFocusState();
    }
  }

  handlePaste = (event: SyntheticClipboardEvent<*>) => {
    const { number, onPaste, emailRequired } = this.props;
    const pastedText = event.clipboardData.getData('text');
    const parsedData = Papa.parse(pastedText).data;
    // If they are only parsing into a single cell, use default paste behavior
    if (_.flatten(parsedData).length > 1) {
      event.preventDefault();
      onPaste(number, parsedData, emailRequired);
    }
  };

  resetFocusState = () => {
    this.setState({ validateBecauseFocusLost: false });
  };

  handleBlur = (event: SyntheticFocusEvent<*>) => {
    const { value, name: fieldName } = event.currentTarget;
    const primaryDeviceInFocus =
      // $FlowFixMe
      event.relatedTarget && event.relatedTarget.name === 'primaryDevice';

    if (this.shouldStoreChange(fieldName, value, this.props.userRow)) {
      const { userRow } = this.props;
      const updatedUserRow = {
        ...userRow,
        [fieldName]: {
          value,
        },
      };

      this.validateAndStoreRow(updatedUserRow, primaryDeviceInFocus);
    }
  };

  shouldStoreChange(fieldName: string, value: string, row: UserRow) {
    const previousValue = this.props.userRow[fieldName].value;

    // Need to always validate the primary device field when the row contains data, because
    // that field can't be empty
    return previousValue !== value || (!userRowEmpty(row) && fieldName === 'primaryDevice');
  }

  validateAndStoreRow(updatedUserRow: UserRow, primaryDeviceInFocus: boolean = false) {
    const { emailRequired, number, onChange } = this.props;

    onChange(number, addValidationErrors(updatedUserRow, emailRequired, primaryDeviceInFocus));
    this.setState({
      nameStored: true,
      primaryDeviceStored: true,
      secondaryDeviceStored: true,
    });
  }

  handleNameChange = () => {
    const { nameStored } = this.state;

    if (nameStored) {
      this.setState({
        nameStored: false,
      });
    }
  };

  handlePrimaryDeviceChange = () => {
    const { primaryDeviceStored } = this.state;

    if (primaryDeviceStored) {
      this.setState({
        primaryDeviceStored: false,
      });
    }
  };

  handleSecondaryDeviceChange = () => {
    const { secondaryDeviceStored } = this.state;

    if (secondaryDeviceStored) {
      this.setState({
        secondaryDeviceStored: false,
      });
    }
  };

  // On keyDown when tab is pushed, react-modal iterates through every
  // component on the DOM looking for where to navigate to.
  // In this large modal, this takes a very long time. We utilize the
  // native form tab functionality, so don't need it.
  handleKeyDown = (event: SyntheticEvent<*>) => {
    event.stopPropagation();
  };

  handleRowBlur = (event: SyntheticFocusEvent<*>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      this.setState({ validateBecauseFocusLost: true });
    }
  };

  render() {
    const { autofocus, namePlaceholder, number, supportSms, userRow, emailRequired } = this.props;
    const { primaryDeviceStored, nameStored, secondaryDeviceStored } = this.state;

    return (
      <Formsy.Form className="UsersParticipantsTableInputRow" onBlur={this.handleRowBlur}>
        <Text
          align="center"
          className="UsersParticipantsTableInputRow-cell--number UsersParticipantsTableInputRow-cell">
          {number}
        </Text>
        <span className="UsersParticipantsTableInputRow-cell">
          <FormCellInput
            autofocus={autofocus}
            name="name"
            placeholder={namePlaceholder}
            value={userRow.name.value}
            warningMessage={nameStored ? userRow.name.errorMessage : null}
            onBlur={this.handleBlur}
            onChange={this.handleNameChange}
            onKeyDown={this.handleKeyDown}
            parsePasteWith={this.handlePaste}
          />
        </span>
        <span className="UsersParticipantsTableInputRow-cell">
          <FormCellInput
            errorMessage={primaryDeviceStored ? userRow.primaryDevice.errorMessage : null}
            name="primaryDevice"
            placeholder={
              emailRequired || !supportSms ? 'Email required' : 'Phone or email required'
            }
            value={userRow.primaryDevice.value}
            onBlur={this.handleBlur}
            onChange={this.handlePrimaryDeviceChange}
            onKeyDown={this.handleKeyDown}
            parsePasteWith={this.handlePaste}
          />
        </span>
        {supportSms && (
          <span className="UsersParticipantsTableInputRow-cell">
            <FormCellInput
              errorMessage={secondaryDeviceStored ? userRow.secondaryDevice.errorMessage : null}
              name="secondaryDevice"
              placeholder="Optional"
              value={userRow.secondaryDevice.value}
              onBlur={this.handleBlur}
              onChange={this.handleSecondaryDeviceChange}
              onKeyDown={this.handleKeyDown}
              parsePasteWith={this.handlePaste}
            />
          </span>
        )}
      </Formsy.Form>
    );
  }
}
